import { Link, useParams } from "react-router-dom";
import useSeo, { SITE_URL } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug = "" } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const cat = article ? categories.find((c) => c.slug === article.category) : undefined;

  const related = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  useSeo({
    title: article ? `${article.title} - 抖漫` : "内容不存在 - 抖漫",
    description: article ? article.excerpt : "您访问的漫画内容不存在。",
    keywords: article ? `抖漫,${article.title},漫画速看,漫画片段` : "抖漫",
    path: article ? `/article/${article.slug}` : `/article/${slug}`,
    type: "article",
    ogImage: article?.cover,
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: article.date,
            dateModified: article.date,
            author: { "@type": "Organization", name: "抖漫" },
            publisher: { "@type": "Organization", name: "抖漫" },
            articleSection: cat?.name ?? "抖漫",
            inLanguage: "zh-CN",
            mainEntityOfPage: `${SITE_URL}/article/${article.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: cat?.name ?? "内容", item: `${SITE_URL}/category/${article.category}` },
              { "@type": "ListItem", position: 3, name: article.title, item: `${SITE_URL}/article/${article.slug}` },
            ],
          },
        ]
      : [],
  });

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
        <h1 className="mt-4 font-heading text-2xl text-foreground-950">内容不存在或已下线</h1>
        <p className="mt-3 text-sm text-foreground-500">您访问的漫画内容可能已被移除。</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm text-background-50 whitespace-nowrap">
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 md:px-6 py-8 md:py-12">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: cat?.name ?? "内容", to: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <header className="mt-6">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
          {cat?.name ?? "抖漫"}
        </span>
        <h1 className="mt-4 font-heading text-2xl md:text-4xl text-foreground-950 leading-snug">{article.title}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
          <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-user-3-line"></i> {article.author}</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-calendar-line"></i> {article.date}</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-time-line"></i> {article.readTime}</span>
          <span className="flex items-center gap-1.5 whitespace-nowrap"><i className="ri-eye-line"></i> {article.views}次观看</span>
        </div>
      </header>

      <figure className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-100">
        <img src={article.cover} alt={article.title} title={`${article.title} - 抖漫`} className="h-full w-full object-cover object-top" />
      </figure>

      <article className="mt-8">
        <p className="text-base md:text-lg leading-8 text-foreground-700">{article.excerpt}</p>
        <div className="mt-8 space-y-8">
          {article.content.map((block) => (
            <section key={block.heading}>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">{block.heading}</h2>
              {block.paragraphs.map((p, i) => (
                <p key={i} className="mt-4 text-sm md:text-base leading-8 text-foreground-700">{p}</p>
              ))}
            </section>
          ))}
        </div>
      </article>

      <div className="mt-8 flex flex-wrap gap-2">
        {article.tags.map((t) => (
          <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">#{t}</span>
        ))}
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-14 border-t border-background-200 pt-10" aria-label="相关推荐">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950">相关推荐</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {related.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </section>
      )}

      <div className="mt-10 flex justify-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full border border-background-200 px-6 py-3 text-sm text-foreground-700 whitespace-nowrap hover:border-primary-300 hover:text-primary-600 transition-colors"
        >
          <i className="ri-arrow-left-line"></i> 返回首页继续逛
        </Link>
      </div>
    </div>
  );
}